<script lang="ts" setup>
import { DateTime } from 'luxon';
import { useChatSettingsStore } from '../../../../runtime/stores/chat-settings';

const rwd = useMedia();
const store = useChatSettingsStore();
const { $log } = useLogging();
const { $alert } = useAlerts();
const { channel } = useSignalR();
const { $t } = useLocalization();

const server = channel('runchat');

const total = ref(0);
const displayTotal = computed<string>(() => total.value > 99 ? '99+' : total.value.toString());

const chat = ref<ChatInteraction[]>([]);
const chatLength = computed(() => chat.value.length);
const userInput = ref<string>(null);

const sending = ref<boolean>(false);

const read = async (): Promise<void> =>
{
    if (total.value > 0)
    {
        total.value--;
    }
};

function newChatUserItem(userMessage: string): void
{
    const newChatUserItem: ChatInteraction = {
        interactionDateUtc: DateTime.now(),
        userInput: userMessage,
        systemResponse: '',
        interactionLogId: null,
        isRated: false,
        imagesDictionary: null
    };

    chat.value.unshift(newChatUserItem);
}

const aiChatEnabled = computed(() => store.aiChatSettings.enableAiChat);

server.on.ChunkReceived = async (payload: UserChatResponse): Promise<void> =>
{
    if (!chat.value.first().imagesDictionary)
    {
        chat.value.first().imagesDictionary = payload.outlineImages;
    }

    if (payload.error)
        chat.value.first().systemResponse += ' ' + payload.error;
    else if (payload.contentPart)
        chat.value.first().systemResponse += payload.contentPart;

    if (payload.isFinal)
    {
        chat.value.first().interactionLogId = payload.interactionLogId;

        sending.value = false;
    }
};

watch(aiChatEnabled, async () =>
{
    if (aiChatEnabled.value)
    {
        messagesLoadData();
    }
});

onMounted(async () =>
{
    messagesLoadData();
});

async function sendMessage(): Promise<void>
{
    try
    {
        if (userInput.value)
        {
            sending.value = true;

            // Dodanie wiadomości użytkownika do czatu
            newChatUserItem(userInput.value);

            // Utworzenie wiadomości
            await AiChatService.chat(userInput.value);

            userInput.value = null;
        }
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

async function messagesLoadData(): Promise<void>
{
    try
    {
        chat.value = await AiChatService.getThread();
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning($t('[[[Wystąpił problem z pobraniem wiadomości.]]]'))
        });
    }
}

function customParser(text: string, images: Record<string, string>): string
{
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    text = text.replace(/^\d+\.\s(.+)/gm, '<li>$1</li>');

    text = text.replace(/(<li>.*<\/li>)/gms, '<ol>$1</ol>');

    text = text.replace(/^\s*-\s(.+)/gm, '<li>$1</li>');

    text = text.replace(/^####\s(.+)/gm, '<br> <h4>$1</h4>');
    text = text.replace(/^###\s(.+)/gm, '<br> <h3>$1</h3>');
    text = text.replace(/^##\s(.+)/gm, '<br> <h2>$1</h2>');
    text = text.replace(/^#\s(.+)/gm, '<br> <h1>$1</h1>');

    if (images)
        text = text.replace(
            /!\[.*?\]\((.+?)\)/g,
            (match, imageInfo) =>
            {
                if (images[imageInfo])
                    return `<br /><img src="${images[imageInfo]}" height="auto" width="500" /><br />`;
            }
        );

    return text;
}

const rateInteraction = async (interactionLogId: number, rate: SystemResponseRatingEnum): Promise<void> =>
{
    try
    {
        await AiChatService.rateInteraction(interactionLogId, rate);

        chat.value.find(x => x.interactionLogId === interactionLogId).isRated = true;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
};
</script>

<template>
    <IdeoDropdown ref="dropdownRef" class="assistant-chat-dropdown ai-enabled" variant="link" no-caret right>
        <template #button-content>
            <span class="position-relative">
                <i class="fa-regular fa-message-bot" />
                <span v-if="total > 0" class="badge text-bg-danger position-absolute top-0 start-100 translate-middle ms-2">{{ displayTotal }}</span>
            </span>
        </template>
        <template v-if="!aiChatEnabled && rwd.desktop()">
            <div>
                <div class="p-3">
                    <div class="fs-5 mb-1 fw-bolder">
                        {{ $t('[[[Wybróbuj możliwości chatu AI]]]') }}
                    </div>

                    <div class="fs-6 mb-3">
                        {{ $t('[[[Przejdź do konfiguracji, aby włączyć]]]') }}
                        <b>{{ $t('[[[Chat AI]]]') }}</b>.
                    </div>

                    <ActionButton variant="fancy" icon="far fa-microchip-ai" :text="$t('[[[Konfiguracja ai]]]')" :to="{ name: 'ai-settings' }" />
                </div>
            </div>
        </template>
        <template v-if="aiChatEnabled">
            <IdeoFormGroup class="pt-2">
                <div ref="chatContainer" class="bg-body chat-container scroll">
                    <div
                        v-for="item in chat"
                        :key="`${item.interactionDateUtc}`"
                        class="chat-item"
                        @mouseover="read()"
                    >
                        <div class="user-message">
                            <div class="mb-0">
                                <strong>{{ $t('[[[Użytkownik]]]') }}</strong>
                            </div>
                            <p :key="item.userInput" v-html="customParser(item.userInput, null)" />
                        </div>
                        <div class="system-message">
                            <div v-if="item.systemResponse" class="mb-1">
                                <strong>{{ $t('[[[System]]]') }}</strong>
                            </div>
                            <p v-if="item.systemResponse" :key="item.systemResponse" v-html="customParser(item.systemResponse, item.imagesDictionary)" />
                        </div>
                        <div class="font-color">
                            <i>{{ $filters.datetime(item.interactionDateUtc) }}</i>
                        </div>
                        <div v-if="item.interactionLogId && !item.isRated" class="rating-buttons">
                            <button type="button" class="navbar-toggler" :title="$t('[[[Ta odpowiedź jest pomocna]]]')" @click="rateInteraction(item.interactionLogId, SystemResponseRatingEnum.Helpful)">
                                <i class="far fa-fw fa-thumbs-up" />
                            </button>
                            <button type="button" class="navbar-toggler" :title="$t('[[[Ta odpowiedź nie jest pomocna]]]')" @click="rateInteraction(item.interactionLogId, SystemResponseRatingEnum.UnHelpful)">
                                <i class="far fa-fw fa-thumbs-down" />
                            </button>
                            <button type="button" class="navbar-toggler" :title="$t('[[[Ta odpowiedź jest powiązana]]]')" @click="rateInteraction(item.interactionLogId, SystemResponseRatingEnum.NotRelated)">
                                <i class="far fa-fw fa-circle-minus" />
                            </button>
                        </div>
                        <div v-if="item.isRated" class="font-color">
                            {{ $t('[[[Dziękujemy za podzielenie się opinią]]]') }}
                        </div>
                    </div>
                    <div v-show="chatLength === 0" class="chat-item">
                        <div class="mb-1">
                            <strong>{{ $t('[[[System]]]') }}</strong>
                        </div>
                        <p>{{ $t('[[[Cześć, jestem Edito Bot. W czym mogę Ci pomóc?]]]') }}</p>
                    </div>
                </div>
            </IdeoFormGroup>

            <IdeoFormGroup class="chat-input">
                <!-- Wiadomość -->
                <IdeoFormTextarea
                    v-model="userInput"
                    name="userInput"
                    rows="2"
                    :placeholder="$t('[[[Zadaj pytanie]]]')"
                    :disabled="sending"
                    @keydown.enter.prevent="sendMessage()"
                />
                <!-- Wyślij wiadomość -->
                <div class="pb-2 mt-2">
                    <ActionButton
                        variant="fancy"
                        icon="far fa-paper-plane"
                        class="p-20 w-100"
                        :disabled="!userInput"
                        :loading="sending"
                        :text="$t('[[[Wyślij wiadomość]]]')"
                        @click.prevent="sendMessage()"
                    />
                </div>
            </IdeoFormGroup>
        </template>
    </IdeoDropdown>
</template>

<style lang="scss">
.assistant-chat-dropdown {
    font-size: 20px;
    .dropdown-menu {
        flex-direction: column;
        justify-content: flex-end;
        position: fixed !important;
        right: 0 !important;
        left: auto !important;
        transform: none !important;
        top: 7vh !important;
        height: calc(100vh - 70px);
        max-height: calc(100vh - 65px);
        overflow: hidden;
    }
    .dropdown {
        &-menu {
            padding: 0.5rem 1rem;
            width: 66vh;
            z-index: 10001;
            height: calc(100vh - 70px);
            max-height: calc(100vh - 65px);
            overflow: auto;
        }
        &-toggle {
            i.fa-message-bot {
                font-size: 1rem;
                color: var(--bs-navbar-color);
            }
        }
        &-item {
            white-space: unset;
            padding: 0.625rem;
            border-radius: 0.313rem;
            background: var(--bs-tertiary-bg);
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin-bottom: 0.5rem;
            }
            &:hover {
                background: var(--bs-secondary-bg);
            }
            i {
                font-size: 1rem;
            }
            .title, .date {
                display: block;
                color: var(--bs-body-color);
            }
            .title {
                margin-bottom: 0.625rem;
            }
        }
        .show-all {
            text-transform: unset;
            font-size: 0.875rem;
            font-weight: 400;
        }
    }
}
.form {
    margin-top: 1rem;

    &:first-child {
        margin-top: 0;
    }
}
.page-pills-options > button {
    &:first-of-type {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    }

    &:last-of-type {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }
}
.chat-container {
    display: flex;
    gap: 12px;
    height: 75vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column-reverse;
    padding-right: 8px;
    padding-top: 0px;
    padding-bottom: 0px;
}
.chat-item {
    background-color: #fafafa;
    padding: 10px 15px;
    padding-bottom: 0px;
    border-radius: 5px;
    transition: background-color 0.3s ease;
}
.chat-item:hover {
    background-color: #f0f0f0;
}
.font-color {
    color: #cfcfcf;
    font-size: 0.8em;
}

.user-message {
    text-align: right;
}

.system-message {
    text-align: left;
}

.chat-container {
    flex: 1; // Zajmuje pozostałą przestrzeń
    overflow-y: auto; // Dodaje przewijanie
    display: flex;
    flex-direction: column-reverse; // Odwraca układ
    padding: 8px; // Dodaje odstępy
}

.chat-input {
    flex-shrink: 0; // Utrzymuje swoją wysokość
    margin-top: auto; // Przylega do dołu
    padding: 2px 0; // Dodaje odstępy
    background-color: #fff; // Opcjonalne tło
    border-top: 1px solid #e0e0e0; // Opcjonalna linia
}

.rating-buttons{
    padding: 0px;
}
</style>
